import Decimal from 'decimal.js'

import {
  distinctPositionUnderlyings,
  hedgeInstrumentScope,
} from '@/features/hedge/lib/hedge-instrument'
import {
  HedgeBandForm,
  HedgeFormModel,
  HedgePriorityBandForm,
  HedgeValidationError,
  PRIORITY_RUNG_KEYS,
  PriorityRungKey,
  RUNG_ORDER,
} from '@/features/hedge/types'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import {
  HedgeBand,
  HedgeDeltaBandsResolved,
  HedgeDeltaDenomination,
  HedgeMechanism,
  HedgePriorityBand,
} from '@/types/generated/algo'
import { Position } from '@/types/generated/common/trading'
import { Exchange, Instrument } from '@/types/generated/common/types'

const SIGNED_DECIMAL = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/

export function isSignedDecimal(value: string): boolean {
  return SIGNED_DECIMAL.test(value.trim())
}

/** A plain decimal string (see {@link isSignedDecimal}) strictly greater than zero. */
export function isPositiveDecimal(value: string): boolean {
  if (!isSignedDecimal(value)) return false
  return new Decimal(value.trim()).greaterThan(0)
}

function emptyPriorityBand(): HedgePriorityBandForm {
  return {
    enabled: false,
    from: '',
    to: '',
    target: '0',
    mechanism: HedgeMechanism.HEDGE_MECHANISM_POST_ONLY_LIMIT,
    twapDurationSeconds: '',
  }
}

// Half-widths (literal delta values in `denomination` units) for the two default-enabled
// lines, matching hedge-presets.ts's LOW/ACCEPTABLE magnitudes so a fresh dialog and a
// "neutral ladder" preset agree.
const DEFAULT_ACCEPTABLE_HALF_WIDTH = 0.1
const DEFAULT_LOW_HALF_WIDTH = 0.5

// Deliberately narrower than risk-slides' DEFAULT_PRICE_RANGE_PCT (state/risk.ts) — the
// delta band chart's simulated curve only needs to span the trigger ladder, not the wider
// range risk-slides' own price-scenario table cares about. Exported so hedge-target-filter.tsx
// can reset to the same default when toggling Price Range back to percent.
export const HEDGE_DEFAULT_PRICE_RANGE_PCT = { from: -5, to: 5 } as const

export function defaultHedgeForm(): HedgeFormModel {
  return {
    hedgeInstrument: '',
    scopeMode: 'portfolio',
    underlyingAsset: '',
    positionSymbols: [],
    positionGroupLabel: '',
    denomination: HedgeDeltaDenomination.HEDGE_DELTA_DENOMINATION_UNDERLYING_UNITS,
    priceRangeKind: 'percent',
    priceRangeFrom: String(HEDGE_DEFAULT_PRICE_RANGE_PCT.from),
    priceRangeTo: String(HEDGE_DEFAULT_PRICE_RANGE_PCT.to),
    priceStep: '10 Parts',
    // Pre-seeded with real numbers (not blank) so the delta chart shows its two default
    // lines — one below target, one above — the moment the dialog opens, no "start from a
    // neutral ladder" step required.
    acceptable: {
      enabled: true,
      from: String(-DEFAULT_ACCEPTABLE_HALF_WIDTH),
      to: String(DEFAULT_ACCEPTABLE_HALF_WIDTH),
      target: '0',
    },
    minHighPriority: emptyPriorityBand(),
    minMediumPriority: emptyPriorityBand(),
    minLowPriority: {
      ...emptyPriorityBand(),
      enabled: true,
      from: String(-DEFAULT_LOW_HALF_WIDTH),
      to: String(-DEFAULT_ACCEPTABLE_HALF_WIDTH),
    },
    maxLowPriority: {
      ...emptyPriorityBand(),
      enabled: true,
      from: String(DEFAULT_ACCEPTABLE_HALF_WIDTH),
      to: String(DEFAULT_LOW_HALF_WIDTH),
    },
    maxMediumPriority: emptyPriorityBand(),
    maxHighPriority: emptyPriorityBand(),
    modeKind: 'deltaStream',
    executionSeconds: '',
    positiveGammaTreatment: false,
    replaceOverlapping: false,
  }
}

// The ladder's identity: every rung's enabled flag and values, in RUNG_ORDER.
function ladderSignature(form: HedgeFormModel): string {
  return JSON.stringify(
    RUNG_ORDER.map((key) => {
      const { enabled, from, to, target } = form[key] as HedgeBandForm
      return [enabled, from, to, target]
    })
  )
}

const DEFAULT_LADDER_SIGNATURE = ladderSignature(defaultHedgeForm())

/** Whether two forms carry the same ladder (rungs, values and enabled flags). */
export function sameLadder(a: HedgeFormModel, b: HedgeFormModel): boolean {
  return ladderSignature(a) === ladderSignature(b)
}

/** Whether the form's ladder is still the untouched default from {@link defaultHedgeForm}. */
export function isDefaultLadder(form: HedgeFormModel): boolean {
  return ladderSignature(form) === DEFAULT_LADDER_SIGNATURE
}

function isPositive(value: string): boolean {
  if (!isSignedDecimal(value)) return false
  return new Decimal(value).greaterThan(0)
}

// Seconds that go on the wire as int64 (interval, TWAP duration).
export function isPositiveWholeNumber(value: string): boolean {
  return isPositive(value) && new Decimal(value).isInteger()
}

interface OrderedRung {
  key: (typeof RUNG_ORDER)[number]
  band: HedgeBandForm
}

/** All enabled rungs (acceptable and priority alike) in RUNG_ORDER sequence. */
export function orderedEnabledRungs(form: HedgeFormModel): OrderedRung[] {
  return RUNG_ORDER.map((key) => ({ key, band: form[key] as HedgeBandForm })).filter(
    (r) => r.band.enabled
  )
}

/** The scope and hedge-instrument checks, shared with the single hedge. */
export function validateHedgeScope(
  form: HedgeFormModel,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeValidationError[] {
  const errors: HedgeValidationError[] = []

  // The hedge instrument is optional — when omitted the backend derives each scoped
  // underlying's perpetual. An explicit override is only valid for a single-underlying
  // scope, and it must match that underlying.
  const instrument = form.hedgeInstrument.trim()
  if (instrument) {
    const scope = hedgeInstrumentScope(form, positions, instrumentMap, multiExchangeInstrumentMap)
    if (!scope.allowsExplicit) {
      errors.push({
        field: 'hedgeInstrument',
        message:
          form.scopeMode === 'portfolio'
            ? 'Remove the override - a whole-portfolio hedge derives one instrument per underlying'
            : 'Remove the override - a multi-underlying group derives one instrument per underlying',
      })
    } else if (
      scope.underlying &&
      resolveBaseCurrency(instrumentMap, instrument) !== scope.underlying
    ) {
      errors.push({
        field: 'hedgeInstrument',
        message: `Hedge instrument must be a ${scope.underlying} instrument`,
      })
    }
  }

  if (form.scopeMode === 'underlying' && !form.underlyingAsset.trim()) {
    errors.push({ field: 'underlyingAsset', message: 'Select an underlying asset' })
  }
  if (form.scopeMode === 'underlying' && form.underlyingAsset.trim()) {
    const heldUnderlyings = distinctPositionUnderlyings(
      positions,
      instrumentMap,
      multiExchangeInstrumentMap
    )
    if (!heldUnderlyings.includes(form.underlyingAsset.trim().toUpperCase())) {
      errors.push({
        field: 'underlyingAsset',
        message: 'Select an underlying with an open position in this account',
      })
    }
  }
  if (form.scopeMode === 'positionGroup' && form.positionSymbols.length === 0) {
    errors.push({ field: 'positionSymbols', message: 'Select at least one position' })
  }
  // A whole-portfolio hedge expands into one request per open underlying (see
  // hedge-builder-dialog.tsx's submissionAssets) — with none open there's nothing to expand
  // into, so submitting would make zero API calls and silently no-op instead of failing.
  if (
    form.scopeMode === 'portfolio' &&
    distinctPositionUnderlyings(positions, instrumentMap, multiExchangeInstrumentMap).length === 0
  ) {
    errors.push({
      field: 'underlyingAsset',
      message: 'No open positions to hedge — select an underlying or position group instead',
    })
  }

  return errors
}

export function validateHedgeForm(
  form: HedgeFormModel,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeValidationError[] {
  const errors = validateHedgeScope(form, positions, instrumentMap, multiExchangeInstrumentMap)

  const enabled = orderedEnabledRungs(form)

  // Per-rung decimal + range validation.
  for (const { key, band } of enabled) {
    for (const part of ['from', 'to', 'target'] as const) {
      if (!isSignedDecimal(band[part])) {
        errors.push({ field: `${key}.${part}`, message: 'Enter a signed decimal' })
      }
    }
    if (isSignedDecimal(band.from) && isSignedDecimal(band.to)) {
      if (!new Decimal(band.from).lessThan(band.to)) {
        errors.push({ field: `${key}.range`, message: '"from" must be less than "to"' })
      }
    }
  }

  // At least one priority rung.
  const enabledPriority = PRIORITY_RUNG_KEYS.filter((k) => form[k].enabled)
  if (enabledPriority.length === 0) {
    errors.push({ field: 'bands', message: 'Add at least one priority band' })
  }

  // TWAP duration.
  for (const key of enabledPriority) {
    const band = form[key]
    if (
      band.mechanism === HedgeMechanism.HEDGE_MECHANISM_TWAP &&
      !isPositiveWholeNumber(band.twapDurationSeconds)
    ) {
      errors.push({
        field: `${key}.twapDurationSeconds`,
        message: 'TWAP duration (seconds) must be a whole number greater than 0',
      })
    }
  }

  // Lower-bound nesting: enabled rungs' `from` strictly increasing in ladder order.
  const fromsValid = enabled.every((r) => isSignedDecimal(r.band.from))
  if (fromsValid) {
    for (let i = 1; i < enabled.length; i++) {
      if (!new Decimal(enabled[i - 1].band.from).lessThan(enabled[i].band.from)) {
        errors.push({
          field: 'nesting',
          message:
            'Band lower bounds must increase: min_high < min_medium < min_low < acceptable < max_low < max_medium < max_high',
        })
        break
      }
    }
  }

  // Only Interval has a user-specified execution duration.
  if (form.modeKind === 'interval' && !isPositiveWholeNumber(form.executionSeconds)) {
    errors.push({
      field: 'executionSeconds',
      message: 'Interval (seconds) must be a whole number greater than 0',
    })
  }

  return errors
}

export type { PriorityRungKey }

export interface NormalizedRung {
  key: (typeof RUNG_ORDER)[number]
  isAcceptable: boolean
  from: number
  to: number
  target: number
  mechanism?: HedgeMechanism
}

const PRIORITY_BAND_BY_KEY: Record<
  PriorityRungKey,
  (b: HedgeDeltaBandsResolved) => HedgePriorityBand | undefined
> = {
  minHighPriority: (b) => b.minHighPriority,
  minMediumPriority: (b) => b.minMediumPriority,
  minLowPriority: (b) => b.minLowPriority,
  maxLowPriority: (b) => b.maxLowPriority,
  maxMediumPriority: (b) => b.maxMediumPriority,
  maxHighPriority: (b) => b.maxHighPriority,
}

/** Ordered enabled rungs (priority + acceptable) parsed to numbers, for the viz. */
export function rungsFromResolvedBands(bands: HedgeDeltaBandsResolved): NormalizedRung[] {
  const out: NormalizedRung[] = []
  for (const key of RUNG_ORDER) {
    if (key === 'acceptable') {
      const a: HedgeBand | undefined = bands.acceptable
      if (a) {
        out.push({
          key,
          isAcceptable: true,
          from: Number(a.from),
          to: Number(a.to),
          target: Number(a.target),
        })
      }
      continue
    }
    const band = PRIORITY_BAND_BY_KEY[key](bands)
    if (band) {
      out.push({
        key,
        isAcceptable: false,
        from: Number(band.from),
        to: Number(band.to),
        target: Number(band.target),
        mechanism: band.mechanism,
      })
    }
  }
  return out
}

export interface LadderSegment extends NormalizedRung {
  widthFrac: number
}

export interface LadderGeometry {
  min: number
  max: number
  segments: LadderSegment[]
}

export function ladderGeometry(rungs: NormalizedRung[]): LadderGeometry {
  if (rungs.length === 0) return { min: 0, max: 0, segments: [] }
  const min = rungs[0].from
  const max = rungs[rungs.length - 1].to
  const span = max - min || 1
  const segments = rungs.map((r) => ({
    ...r,
    widthFrac: (r.to - r.from) / span,
  }))
  return { min, max, segments }
}

export function markerFraction(value: number, min: number, max: number): number {
  const span = max - min
  if (span <= 0) return 0
  return Math.min(1, Math.max(0, (value - min) / span))
}
